import { motion } from 'motion/react'

import { ExerciseArtifactCard } from './ExerciseArtifactCard'
import { MusclesArtifactCard } from './MusclesArtifactCard'
import { ProgressionArtifactCard } from './ProgressionArtifactCard'
import { RecordArtifactCard } from './RecordArtifactCard'
import { SessionArtifactCard } from './SessionArtifactCard'
import type { CoachArtifact } from './types'
import { VideoArtifactCard } from './VideoArtifactCard'

/**
 * One artifact, by type. Cases are in registry order.
 *
 * The `default` branch is unreachable through `decodeArtifact`, which already
 * rejects an unknown type — it is the second gate of design spec §3.2's
 * "clients MUST ignore artifacts with an unknown type … rendering nothing
 * rather than erroring", kept so this component is safe to call with an
 * artifact assembled some other way.
 *
 * **It is not decoration, and it is not dead now that all six registry types have
 * a case.** Design §4.1 is what decouples the backend's release cadence from
 * either client's: the day a seventh type is registered, this branch is the
 * difference between an old client drawing nothing and an old client throwing
 * mid-stream. The union is whatever this client renders today, never the
 * registry. KNOWN_UNRENDERED in __tests__/artifactContract.test.tsx is where such
 * a gap gets declared; it is empty as of Spec I's Task 2.
 *
 * `ProgressionArtifactCard` can itself return `null` (too few points to draw a
 * line); the switch returns its result directly so that nullability propagates
 * rather than being swallowed into an empty card.
 */
export function ArtifactRenderer({ artifact }: { artifact: CoachArtifact }) {
  switch (artifact.type) {
    case 'session':
      return <SessionArtifactCard data={artifact.data} />
    case 'record':
      return <RecordArtifactCard data={artifact.data} />
    case 'progression':
      return <ProgressionArtifactCard data={artifact.data} />
    case 'exercise':
      return <ExerciseArtifactCard data={artifact.data} />
    case 'video':
      return <VideoArtifactCard data={artifact.data} />
    case 'muscles':
      return <MusclesArtifactCard data={artifact.data} />
    default:
      return null
  }
}

/**
 * Every card attached to one assistant turn.
 *
 * No cap here: the 3-per-turn limit is enforced in the backend dispatch loop
 * (`MAX_ARTIFACTS_PER_TURN`), and a second limit on this side would silently
 * swallow cards the day that number changes.
 *
 * Keyed on the envelope `id`, which the backend mints unique per emission.
 * Entrance motion lives here rather than in the cards so each card stays a
 * pure presentational leaf.
 *
 * An artifact whose `ArtifactRenderer` returns `null` still occupies a
 * `motion.div`, so the column gap would show a blank slot. In practice it
 * cannot happen — `decodeArtifacts` drops undecodable entries before this list
 * ever sees them — which is why there is no filter here and why the test that
 * pins it lives on the decoder.
 *
 * `gap: 12` is V3's bubble column gap. The list sits inside that column as one
 * more child, so matching it makes prose→card and card→card spacing identical
 * and the nesting invisible. (V2 used 14 in a transparent column with no
 * enclosing rhythm to match.)
 */
export function ArtifactList({ artifacts }: { artifacts?: CoachArtifact[] }) {
  if (!artifacts || artifacts.length === 0) return null
  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {artifacts.map((artifact) => (
        <motion.div
          key={artifact.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          <ArtifactRenderer artifact={artifact} />
        </motion.div>
      ))}
    </div>
  )
}

/**
 * Cards anchored between two paragraphs of the reply (Spec K Task 5).
 *
 * Same cards, different entrance, for one reason: **there is text below these.**
 * The reveal is atomic and cards arrive after `done`, so one of these appears
 * inside prose the user is already reading, and anything that is not the last
 * child pushes the rest of the reply down. `ArtifactList`'s opacity+`y` fade
 * would land that push in a single frame — the paragraph being read jumps.
 *
 * So the height animates open too, and the text below slides instead. This is
 * the one place in the coach worth animating a non-composited property: it
 * happens once per card, never on a gesture or a repeated interaction, and the
 * alternative is a visible jump. `overflow: hidden` keeps the card clipped to
 * the growing box rather than spilling over the paragraph beneath it.
 *
 * Trailing cards keep the cheaper fade — nothing sits below them to push.
 */
export function AnchoredArtifacts({ artifacts }: { artifacts: CoachArtifact[] }) {
  if (artifacts.length === 0) return null
  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {artifacts.map((artifact) => (
        <motion.div
          key={artifact.id}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          style={{ overflow: 'hidden' }}
        >
          <ArtifactRenderer artifact={artifact} />
        </motion.div>
      ))}
    </div>
  )
}
